import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Send, Loader2, Phone, Wallet, Lock, CheckCircle2, ArrowLeft, Zap, MessageCirclePlus } from "lucide-react";

const DURATIONS = [
  { v: 30, l: "30 min" }, { v: 60, l: "1 h" }, { v: 120, l: "2 h" }, { v: 240, l: "4 h" }, { v: 1440, l: "24 h" },
];
const brl = (v) => (v == null ? "—" : Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }));
const dbr = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join("/") : "—");

/** Inicia um atendimento ATIVO: o atendente escolhe o cliente, escreve a 1a mensagem
 *  e o bot ja fica em silencio nessa conversa. */
export function NewConversationDialog({ open, onClose, onStarted }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [info, setInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [phone, setPhone] = useState("");
  const [text, setText] = useState("");
  const [minutes, setMinutes] = useState(120);
  const [sending, setSending] = useState(false);
  const timer = useRef(null);

  const reset = () => { setQ(""); setResults([]); setInfo(null); setPhone(""); setText(""); setMinutes(120); };
  useEffect(() => { if (!open) reset(); }, [open]);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/clients", { params: { search: q.trim(), limit: 8 } });
        setResults(data.items || []);
      } catch (ex) { toast.error(formatApiError(ex)); }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(timer.current);
  }, [q]);

  const pick = async (c) => {
    setLoadingInfo(true);
    try {
      const { data } = await api.get("/chat-sessions/outbound/prepare", { params: { login: c.login } });
      setInfo(data);
      setPhone(data.phones[0]?.number || "");
      setMinutes(data.default_minutes || 120);
      setText("");
    } catch (ex) { toast.error(formatApiError(ex)); }
    finally { setLoadingInfo(false); }
  };

  const send = async () => {
    if (!phone || !text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/chat-sessions/outbound", { phone, text, login: info.client.login, minutes });
      toast.success(`Mensagem enviada para ${data.client_name || phone}. Bot em silêncio nesta conversa.`);
      onStarted?.(data.phone);
      onClose();
    } catch (ex) { toast.error(formatApiError(ex)); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-950 border-slate-800 text-slate-100 max-w-xl" data-testid="new-conversation-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit flex items-center gap-2">
            <MessageCirclePlus className="w-5 h-5 text-emerald-400" /> Novo atendimento
          </DialogTitle>
        </DialogHeader>

        {!info && (
          <div className="space-y-3">
            <p className="text-xs text-slate-400">Procure o cliente por nome, login, CPF ou telefone. Ao enviar a primeira mensagem, o bot fica em pausa nessa conversa.</p>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-slate-500" />
              <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar cliente…"
                className="pl-9 bg-slate-900 border-slate-700" data-testid="new-conv-search" />
              {(searching || loadingInfo) && <Loader2 className="w-4 h-4 absolute right-3 top-3 animate-spin text-slate-500" />}
            </div>
            <div className="max-h-72 overflow-y-auto divide-y divide-slate-800 rounded-md border border-slate-800">
              {results.length === 0 && q.trim().length >= 2 && !searching && (
                <div className="p-4 text-center text-sm text-slate-500">Nenhum cliente encontrado.</div>
              )}
              {results.map((c) => (
                <button key={c.login} onClick={() => pick(c)} className="w-full text-left px-3 py-2.5 hover:bg-slate-800/70 flex items-center justify-between gap-2">
                  <span>
                    <span className="block text-sm text-white">{c.nome}</span>
                    <span className="block text-xs text-slate-500 font-mono-jb">{c.login} · {c.celular || c.fone || "sem telefone"}</span>
                  </span>
                  {c.bloqueado === "sim" && <Badge className="bg-red-500/10 text-red-300 border border-red-500/30">bloqueado</Badge>}
                </button>
              ))}
            </div>
          </div>
        )}

        {info && (
          <div className="space-y-3">
            <button onClick={() => setInfo(null)} className="text-xs text-slate-400 hover:text-white flex items-center gap-1">
              <ArrowLeft className="w-3 h-3" /> trocar cliente
            </button>
            <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold text-white">{info.client.nome}</div>
                <div className="flex gap-1">
                  <Badge className={info.client.ativo ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" : "bg-slate-700/40 text-slate-300"}>
                    {info.client.ativo ? "ativo" : "inativo"}
                  </Badge>
                  {info.client.bloqueado && <Badge className="bg-red-500/10 text-red-300 border border-red-500/30"><Lock className="w-3 h-3 mr-1" />bloqueado</Badge>}
                </div>
              </div>
              <div className="text-xs font-mono-jb text-emerald-300 mt-0.5">{info.client.login}</div>
              <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-slate-400">
                <div className="flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" />
                  {info.open_invoice
                    ? <span>{brl(info.open_invoice.valor)} · venc. {dbr(info.open_invoice.vencimento)}
                        {info.open_invoice.dias_atraso > 0 && <span className="text-red-300"> ({info.open_invoice.dias_atraso}d em atraso)</span>}</span>
                    : <span className="text-emerald-300">Sem fatura em aberto</span>}
                </div>
                <div className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" />
                  {info.last_payment ? <span>Últ. pgto {dbr(info.last_payment.data)} · {brl(info.last_payment.valor)}</span> : "Sem pagamentos"}
                </div>
              </div>
            </div>

            {info.active_session && (
              <div className="text-xs rounded-md border border-amber-500/30 bg-amber-500/10 text-amber-200 p-2">
                Já existe um atendimento aberto com este número — a mensagem será enviada nele.
              </div>
            )}

            {info.phones.length === 0 ? (
              <div className="text-sm text-red-300">Este cliente não tem telefone válido no mk-auth.</div>
            ) : (
              <>
                <div className="flex flex-wrap gap-2">
                  {info.phones.map((p) => (
                    <button key={p.number} onClick={() => setPhone(p.number)}
                      className={`px-2.5 py-1 rounded-full text-xs border flex items-center gap-1 ${phone === p.number ? "bg-emerald-500/20 border-emerald-500/60 text-emerald-200" : "border-slate-700 text-slate-400 hover:text-white"}`}>
                      <Phone className="w-3 h-3" /> {p.display} <span className="text-slate-500">· {p.label}</span>
                    </button>
                  ))}
                </div>

                <div className="flex flex-wrap gap-1.5">
                  {info.quick_replies.map((r, i) => (
                    <button key={i} disabled={r.missing?.length > 0} onClick={() => setText(r.text)}
                      title={r.missing?.length ? `Faltam dados: ${r.missing.join(", ")}` : r.text}
                      className="px-2 py-1 rounded-full text-xs border border-slate-700 text-slate-300 hover:border-emerald-500/60 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1">
                      <Zap className="w-3 h-3 text-emerald-400" />{r.label}
                    </button>
                  ))}
                </div>

                <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={5}
                  placeholder="Escreva a primeira mensagem…" className="bg-slate-900 border-slate-700" data-testid="new-conv-text" />

                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-xs text-slate-400 flex items-center gap-1.5 flex-wrap">
                    Manter o bot em silêncio por
                    {DURATIONS.map((d) => (
                      <button key={d.v} onClick={() => setMinutes(d.v)}
                        className={`px-2 py-0.5 rounded-full border ${minutes === d.v ? "border-emerald-500/60 text-emerald-200 bg-emerald-500/10" : "border-slate-700 text-slate-400"}`}>{d.l}</button>
                    ))}
                  </div>
                  <Button onClick={send} disabled={sending || !text.trim() || !phone}
                    className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950" data-testid="new-conv-send">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />} Enviar e abrir conversa
                  </Button>
                </div>
                <p className="text-[11px] text-slate-500">A janela renova sozinha quando o cliente responde. Para devolver ao bot, use "Encerrar agora".</p>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
